/**
 * ScriptEditor — Full-page script editor at /scripts/:id/edit.
 * Renders UnifiedStepEditor connected to a real script via API.
 * Optionally shows Watch Mode (CDP stream + editor split pane) via toggle.
 * Toggle on via the Watch Mode button or ?watch=true query param.
 */

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, IconButton, Tooltip, Paper, Breadcrumbs, Link, TextField, Chip,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button,
  FormControlLabel, Checkbox,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  ConnectedTv as WatchOnIcon,
  TvOff as WatchOffIcon,
} from '@mui/icons-material';
import { UnifiedStepEditor } from '../components/UnifiedStepEditor';
import { WatchModeView } from '../components/WatchMode';
import extensionScriptsService from '../services/api/extensionScriptsService';

/** Send a message to the Automatrix Chrome extension via postMessage bridge */
const sendToExtension = (payload) => {
  return new Promise((resolve, reject) => {
    const requestId = `req_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const timeout = setTimeout(() => {
      window.removeEventListener('message', handler);
      reject(new Error('Extension did not respond. Is it installed?'));
    }, 10000);
    const handler = (event) => {
      if (event.source !== window) return;
      const data = event.data;
      if (!data || data.source !== 'automatrix-extension' || data.requestId !== requestId) return;
      clearTimeout(timeout);
      window.removeEventListener('message', handler);
      resolve(data.payload);
    };
    window.addEventListener('message', handler);
    window.postMessage({ source: 'automatrix-frontend', requestId, payload }, '*');
  });
};

const ScriptEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [scriptName, setScriptName] = useState('');
  const [scriptData, setScriptData] = useState(null);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [pendingRun, setPendingRun] = useState(null); // show hint dialog when sidebar not detected
  const nameInputRef = useRef(null);

  // Watch Mode: initialize from ?watch=true query param.
  // ?engine=v4 (default) launches Chromium with the v4 learner extension;
  // ?engine=playwright runs pure Playwright (clean baseline).
  const [watchMode, setWatchMode] = useState(searchParams.get('watch') === 'true');
  const watchEngineParam = searchParams.get('engine');
  // Normalize: 'v4' | 'extension-v4' → 'v4' (production-mirror, no step mode);
  // anything else (including missing) → 'playwright' (fast clean baseline that
  // supports step-by-step execution — the right default for Watch/Step mode).
  const watchEngine =
    watchEngineParam === 'v4' || watchEngineParam === 'extension-v4'
      ? 'v4'
      : 'playwright';

  const toggleWatchMode = () => {
    const next = !watchMode;
    setWatchMode(next);
    if (next) {
      setSearchParams({ watch: 'true', engine: watchEngine }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  };

  useEffect(() => {
    extensionScriptsService
      .getOne(id)
      .then(({ data }) => {
        setScriptName(data.name || `Script #${id}`);
        setScriptData(data);
        // Auto-open Watch Mode for backend scripts
        if (data.executionMode === 'backend' && !watchMode) {
          setWatchMode(true);
          setSearchParams({ watch: 'true', engine: watchEngine }, { replace: true });
        }
      })
      .catch(() => setScriptName(`Script #${id}`));
  }, [id]);

  const handleStartEditing = () => {
    setEditValue(scriptName);
    setIsEditingName(true);
  };

  useEffect(() => {
    if (isEditingName && nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
    }
  }, [isEditingName]);

  const handleSaveName = async () => {
    const trimmed = editValue.trim();
    if (!trimmed || trimmed === scriptName) {
      setIsEditingName(false);
      return;
    }
    try {
      await extensionScriptsService.update(id, { name: trimmed });
      setScriptName(trimmed);
    } catch (err) {
      console.error('Failed to update script name:', err);
    }
    setIsEditingName(false);
  };

  const doRunInExtension = useCallback(async (credentialId, variables) => {
    try {
      const token = localStorage.getItem('token');
      if (token) await sendToExtension({ action: 'SET_AUTH_TOKEN', token }).catch(() => {});
      const resp = await sendToExtension({
        action: 'RUN_SCRIPT',
        scriptId: id,
        credentialVaultId: credentialId || undefined,
        variables: variables || {},
      });
      if (resp && resp.success === false) alert('Extension error: ' + (resp.error || 'unknown'));
    } catch (err) {
      alert('Could not reach extension: ' + err.message);
    }
  }, [id]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveName();
    } else if (e.key === 'Escape') {
      setIsEditingName(false);
    }
  };

  return (
    <Box sx={{ height: watchMode ? 'calc(100vh - 100px)' : 'auto' }}>
      {/* Breadcrumb navigation */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: watchMode ? 1.5 : 2 }}>
        <Tooltip title="Back to Scripts">
          <IconButton size="small" onClick={() => navigate('/scripts')}>
            <BackIcon />
          </IconButton>
        </Tooltip>
        <Breadcrumbs>
          <Link
            underline="hover"
            color="inherit"
            sx={{ cursor: 'pointer' }}
            onClick={() => navigate('/scripts')}
          >
            Scripts
          </Link>
          {isEditingName ? (
            <TextField
              inputRef={nameInputRef}
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={handleSaveName}
              onKeyDown={handleKeyDown}
              variant="standard"
              size="small"
              sx={{
                minWidth: 200,
                '& .MuiInputBase-input': {
                  fontSize: '0.875rem',
                  fontWeight: 600,
                },
              }}
            />
          ) : (
            <Tooltip title="Click to rename">
              <Typography
                color="text.primary"
                variant="body2"
                fontWeight={600}
                onClick={handleStartEditing}
                sx={{
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  '&:hover': { color: 'primary.main' },
                }}
              >
                {scriptName ? `Edit Script \u2014 ${scriptName}` : 'Edit Script'}
                <EditIcon sx={{ fontSize: 14, opacity: 0.5 }} />
              </Typography>
            </Tooltip>
          )}
        </Breadcrumbs>
        {watchMode && (
          <Chip label="WATCH MODE" size="small" color="info" sx={{ fontWeight: 700 }} />
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title={watchMode ? 'Close Watch Mode' : 'Open Watch Mode'}>
          <IconButton
            size="small"
            color={watchMode ? 'info' : 'default'}
            onClick={toggleWatchMode}
            sx={{
              border: watchMode ? '2px solid' : '1px solid #ccc',
              borderColor: watchMode ? 'info.main' : '#ccc',
              borderRadius: 1,
              px: 1,
            }}
          >
            {watchMode ? <WatchOffIcon fontSize="small" /> : <WatchOnIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Content: Watch Mode split pane OR editor-only */}
      {watchMode ? (
        <Box sx={{ height: 'calc(100% - 48px)' }}>
          <WatchModeView
            scriptId={id}
            scriptName={scriptName}
            engine={watchEngine}
          />
        </Box>
      ) : (
        <Paper elevation={2} sx={{ height: 'calc(100vh - 180px)', overflow: 'hidden' }}>
          <UnifiedStepEditor
            scriptId={id}
            onRunInExtension={(steps, variables, credentialId) => {
              (async () => {
                doRunInExtension(credentialId, variables);
                if (localStorage.getItem('skipSidebarPrompt') === '1') return;
                try {
                  const resp = await sendToExtension({ action: 'IS_SIDE_PANEL_OPEN' });
                  if (resp?.open) return;
                } catch {}
                setPendingRun({ credentialId, variables });
              })();
            }}
            onWatchMode={(steps, vars, credId, engine) => {
              // engine: 'extension-v4' | 'playwright' from LaunchBar chevron.
              // Normalize to URL form: 'v4' | 'playwright'. Default v4.
              const normalized = engine === 'playwright' ? 'playwright' : 'v4';
              setWatchMode(true);
              setSearchParams(
                { watch: 'true', engine: normalized },
                { replace: true },
              );
            }}
            onRunHeadless={async (steps, variables, credentialId, mode = 'playwright') => {
              try {
                await extensionScriptsService.runByExecutor(id, mode, {
                  credentialVaultId: credentialId || undefined,
                });
                const label = mode === 'extension-v5' ? 'v5 extension' : 'Playwright';
                alert(`Headless run started (${label}). Check the History tab for results.`);
              } catch (err) {
                console.error('Headless run failed:', err);
                alert('Headless run failed: ' + (err.message || 'unknown error'));
              }
            }}
            onCredentialChange={(credentialVaultId, appKey) => {
              // Sync credential selection to extension sidebar
              sendToExtension({
                action: 'SELECT_CREDENTIAL',
                credentialVaultId,
                appKey,
              }).catch(() => {}); // Ignore if extension not available
            }}
          />
        </Paper>
      )}

      <Dialog open={!!pendingRun} onClose={() => setPendingRun(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Open the Automatrix sidebar</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The script has been loaded into the extension. To run it:
          </DialogContentText>
          <DialogContentText component="ol" sx={{ mt: 1, pl: 3 }}>
            <li>Click the <strong>Automatrix icon</strong> in your Chrome toolbar (or pin it from the puzzle-piece menu)</li>
            <li>Pick a credential if needed</li>
            <li>Click <strong>Play</strong> in the sidebar to start</li>
          </DialogContentText>
          <FormControlLabel
            sx={{ mt: 2 }}
            control={
              <Checkbox
                onChange={(e) => {
                  if (e.target.checked) localStorage.setItem('skipSidebarPrompt', '1');
                  else localStorage.removeItem('skipSidebarPrompt');
                }}
              />
            }
            label="Don't ask again"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingRun(null)}>OK</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ScriptEditor;
